import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CommunityPost } from "../interfaces/post.interface";
import { usePostStore } from "../../../../../store/postStore";

type Props = {
  post: CommunityPost | null;
  isOpen: boolean;
  onClose: () => void;
};

const DeletePostDialog: React.FC<Props> = ({ post, isOpen, onClose }) => {
  const deletePost = usePostStore((s) => s.deletePost);
  const isSaving = usePostStore((s) => s.isSaving);

  const handleConfirm = async () => {
    if (!post) return;

    const success = await deletePost(post.id);

    if (success) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white dark:bg-dark_grey p-6 shadow-xl">
                <Dialog.Title className="text-xl font-medium mb-2">
                  Delete Post
                </Dialog.Title>

                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete this post? This action cannot be undone.
                </p>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded border"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleConfirm}
                    disabled={isSaving}
                    className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                  >
                    {isSaving ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeletePostDialog;